import React, { useContext, useEffect, useState } from "react";
import {
  createGroup,
  createPerson,
  getGroupInvite,
  regenerateGroupInvite,
  removeGroupMember,
} from "../../api";
import { useCurrentUser } from "../../CurrentUserContext";
import { MealPlannerContext } from "../../contexts";
import { Button } from "../ui/Button";
import { useToast } from "../ui/Toast";

/**
 * Gruppenverwaltung im Einstellungen-Drawer: aktive Gruppe waehlen,
 * Einladungslink anzeigen/erneuern, Mitglieder verwalten und Personen ohne
 * eigenen Login anlegen.
 */
export const GroupSection = () => {
  const {
    groups,
    activeGroup,
    activeGroupId,
    members,
    personId,
    refresh,
  } = useCurrentUser();
  const { requestGroupChange } = useContext(MealPlannerContext);
  const toast = useToast();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [newPersonName, setNewPersonName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (activeGroupId == null || activeGroup?.personal) {
      setInviteToken(null);
      return;
    }
    (async () => {
      try {
        const invite = (
          await getGroupInvite({ path: { groupId: activeGroupId } })
        ).data;
        if (!cancelled) {
          setInviteToken(invite.token ?? null);
        }
      } catch {
        if (!cancelled) {
          setInviteToken(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId, activeGroup?.personal]);

  const resetInvite = async () => {
    if (activeGroupId == null) return;
    const invite = (
      await regenerateGroupInvite({ path: { groupId: activeGroupId } })
    ).data;
    setInviteToken(invite.token ?? null);
    toast({ title: "Neuer Einladungslink erstellt", status: "success" });
  };

  const copyInvite = async () => {
    if (!inviteToken) return;
    const url = `${window.location.origin}/?invite=${inviteToken}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Einladungslink kopiert", status: "success" });
    } catch {
      toast({ title: url, status: "info", isClosable: true });
    }
  };

  const addPerson = async () => {
    const name = newPersonName.trim();
    if (!name || activeGroupId == null) return;
    await createPerson({
      body: {
        name,
        active: true,
        need: { personId: 0, fat: 70, carbs: 250, protein: 100, kcal: 2000 },
      },
      query: { groupId: activeGroupId },
    });
    setNewPersonName("");
    refresh();
    toast({ title: `Person „${name}" hinzugefügt`, status: "success" });
  };

  const removeMember = async (memberId: number) => {
    if (activeGroupId == null) return;
    await removeGroupMember({
      path: { groupId: activeGroupId, personId: memberId },
    });
    refresh();
  };

  const addGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    const created = (await createGroup({ body: { name } })).data;
    setNewGroupName("");
    if (created.id != null) {
      requestGroupChange(created.id);
    }
    refresh();
    toast({ title: `Gruppe „${name}" angelegt`, status: "success" });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm font-bold text-ink">Gruppe</p>

      {/* Gruppenauswahl */}
      <div className="flex flex-wrap gap-2">
        {groups.map((group) => {
          const active = group.id === activeGroupId;
          return (
            <button
              key={group.id}
              data-cy="group-chip"
              onClick={() => group.id != null && requestGroupChange(group.id)}
              className={`px-3 py-1.5 rounded-control border text-sm transition-colors ${
                active
                  ? "border-teal bg-teal/5 text-ink font-semibold"
                  : "border-line bg-surface-card text-ink-muted hover:bg-surface-raised"
              }`}
            >
              {group.personal ? "Nur ich" : group.name}
            </button>
          );
        })}
      </div>

      {/* Neue (geteilte) Gruppe anlegen */}
      <div className="flex gap-2">
        <input
          value={newGroupName}
          onChange={(event) => setNewGroupName(event.target.value)}
          placeholder="Neue Gruppe (z. B. Haushalt)"
          data-cy="group-add-group-input"
          className="flex-1 rounded-control border border-line bg-surface-card px-3 py-2 text-sm text-ink"
        />
        <Button size="sm" onClick={addGroup} dataCy="group-add-group-button">
          Anlegen
        </Button>
      </div>

      {/* Einladungslink (nicht für persönliche Gruppe) */}
      {!activeGroup?.personal && inviteToken && (
        <div className="flex flex-col gap-2 rounded-card border border-line p-3">
          <span className="text-xs text-ink-muted">Einladungslink</span>
          <span
            className="text-xs text-ink break-all"
            data-cy="group-invite-link"
          >
            {`${window.location.origin}/?invite=${inviteToken}`}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="tertiary" onClick={copyInvite}>
              Kopieren
            </Button>
            <Button size="sm" variant="tertiary" onClick={resetInvite}>
              Neu erzeugen
            </Button>
          </div>
        </div>
      )}

      {/* Mitglieder */}
      <div className="flex flex-col gap-2" data-cy="group-member-list">
        {members.map((member) => (
          <div
            key={member.id}
            data-cy="group-member"
            className="flex items-center justify-between gap-2 rounded-control bg-surface-card px-3 py-2"
          >
            <span className="text-sm text-ink">
              {member.name}
              {member.id === personId ? " (ich)" : ""}
            </span>
            {member.id !== personId && (
              <button
                onClick={() => removeMember(member.id)}
                className="text-xs text-protein hover:underline"
                data-cy="group-member-remove"
              >
                Entfernen
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Person ohne Login hinzufügen */}
      <div className="flex gap-2">
        <input
          value={newPersonName}
          onChange={(event) => setNewPersonName(event.target.value)}
          placeholder="Name der Person"
          data-cy="group-add-person-input"
          className="flex-1 rounded-control border border-line bg-surface-card px-3 py-2 text-sm text-ink"
        />
        <Button size="sm" onClick={addPerson} dataCy="group-add-person-button">
          Hinzufügen
        </Button>
      </div>
    </div>
  );
};
